import Box from '@mui/material/Box';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetStockTransferService } from 'subModule/src/services/inventory/useInventory';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import Loader from 'layout/Loader';
import BackendPaginationTable from 'common/Table/TableWithPagination';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import MoveDownOutlinedIcon from '@mui/icons-material/MoveDownOutlined';
import { useNavigate } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';
import StockTransferSearchHeader from 'components/Inventory/StockTransferSearchHeader';
import { useGetStoreDropdownService } from 'subModule/src/services/store/useStore';

interface SearchFormData {
    [key: string]: string | number;
}
export default function StockTransferListing() {
    const navigate = useNavigate();
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [filter, setFilter] = useState<string[]>(['sourceStoreId', 'destinationStoreId']);
    const [search, setSearch] = useState<any>({});
    const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>('');
    const [dropdownPage, setDropdownPage] = useState<number>(1);
    const [searchLoading, setSearchLoading] = useState<string | null>(null);
    const [searchFormData, setSearchFormData] = useState<SearchFormData>({ sourceStoreId: 0, destinationStoreId: 0, storeTitle: '', destinationStoreTitle: '' });
    const { data: tableData, isFetching, refetch } = useGetStockTransferService({ pageNumber: pageNo, size: pageSize, ...search });
    const { data: storesData, refetch: storesRefetch } = useGetStoreDropdownService({ pageNumber: dropdownPage, size: 50, title: dropdownSearchTerm });
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        });
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, search]);
    useEffect(() => {
        storesRefetch();
    }, []);
    const handleDropdownActions = (typeName: string, action: 'searchIt' | 'loadNext', search?: string) => {
        if(action === 'searchIt') {
            setDropdownPage(1);
            setDropdownSearchTerm(search);
            if(search) setSearchLoading(typeName);
        } else {
            setDropdownPage(dropdownPage + 1);
            setSearchLoading(typeName);
        }
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            if(typeName === 'stores' || typeName === 'destinationStores') storesRefetch().finally(() => setSearchLoading(null));
        }, 300);
    }
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const showRowView = (row: any) => navigate('/inventory/stock-transfer/' + row.id + '/print');
    const clearSearch = () => {
        setFilter(['sourceStoreId', 'destinationStoreId']);
        setSearchFormData({ sourceStoreId: 0, destinationStoreId: 0, storeTitle: searchFormData.storeTitle, destinationStoreTitle: searchFormData.destinationStoreTitle });
        setSearch({});
    }
    const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
        const { target: { value }, } = event;
        setFilter(typeof value === 'string' ? value.split(',') : value,);
        setSearchFormData(prevFormData =>
            Object.fromEntries(Object.keys(prevFormData).map(key => 
                [key, value.includes(key) ? prevFormData[key] : (typeof prevFormData[key] === 'string' ? '' : 0)]
            ))
        );
        if (!value.length && Object.keys(search).length) {
            clearSearch();
        }
    };
    const handleChangeSearch = (field: string, value: any) => {
        const val = field === 'sourceStoreId' || field === 'destinationStoreId' ? (value ? parseInt(value.value) || 0 : 0) : value;
        setSearchFormData((prevData) => ({
            ...prevData,
            [field]: val,
            ...(field === 'sourceStoreId' && value?.label ? { storeTitle: value.label } : {}),
            ...(field === 'destinationStoreId' && value?.label ? { destinationStoreTitle: value.label } : {}),
        }));
    };
    const handleSubmitSearch = () => {
        const filteredSearch: Record<string, string | number> = {};
        Object.entries(searchFormData).forEach(([key, value]) => {
            if (value) {
              filteredSearch[key] = typeof value === 'string' ? value?.trim() : value;
            }
        });
        setSearch(filteredSearch);
        setPageNo(1);
        setPageSize(pageSize);
    };

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/inventory/stock-transfer')}
                heading={t('StockTransfers')}
                icon={<MoveDownOutlinedIcon sx={{ fill: '#000', width: '30px', height: '30px', marginTop: '3px' }} />}
                btnText={"create"}
                btnType={"create"}
                showCreateBtn={checkPrivileges("ROLE_STOCKTRANSFER_CREATE")}
            />
            <Box>
                {isFetching ? <Loader /> :
                    <div id="mainListingTable">
                        <BackendPaginationTable
                            columns={[
                                { id: 'sourceStoreTitle', label: t('from') + ' ' + t('store'), numeric: false, width: '18%' },
                                { id: 'destinationStoreTitle', label: t('to') + ' ' + t('store'), numeric: false, width: '18%' },
                                { id: 'totalQuantity', label: 'totalQuantity', numeric: false, width: '12%' },
                                { id: 'transferDate', label: 'date', numeric: false, width: '14%' },
                                { id: 'createdAt', label: 'createdAt', numeric: false, width: '20%' },
                                { id: 'createdBy', label: 'createdBy', numeric: false, width: '18%' },
                            ]}
                            data={tableData?.pages[0]?.data?.content || []}
                            pageNo={pageNo}
                            pageSize={pageSize}
                            tableData={tableData?.pages[0]?.data || {}}
                            showCheckbox={false}
                            showActions={true}
                            enableSorting={true}
                            showHeader={true}
                            showSearch={true}
                            showFilter={true}
                            showCreateButton={true}
                            handleChangePage={handleChangePage}
                            handleChangeRows={handleChangeRows}
                            rowClick={checkPrivileges("ROLE_STOCKTRANSFER_VIEW")}
                            showRowView={showRowView}
                            loadingSearch={false}
                            headerComponent={
                                <StockTransferSearchHeader
                                    searchFormData={searchFormData}
                                    handleChangeFilter={handleChangeFilter}
                                    handleChangeSearch={handleChangeSearch}
                                    handleSubmitSearch={handleSubmitSearch}
                                    clearSearch={clearSearch}
                                    storesData={storesData?.pages[0]?.data?.content || []}
                                    storesResponse={storesData || {}}
                                    handleDropdownActions={handleDropdownActions}
                                    {...{ filter, search, dropdownPage, searchLoading, dropdownSearchTerm, setDropdownSearchTerm, setDropdownPage }}
                                />
                            }
                        />
                    </div>
                }
            </Box>
        </Box>
    )
}